<script lang="ts">
    import { faPlusCircle, faEdit } from '@fortawesome/free-solid-svg-icons'
    import Content from '../_common/content.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import { onMount } from 'svelte'
    import type { Person } from '../../modules/eventsApi/persons/person'
    import { PersonsRepository } from '../../modules/eventsApi/persons/persons.repository'
    import { PersonsService } from '../../modules/eventsApi/persons/persons.service'
    import dayjs from 'dayjs'
    import DateSelect from '../_common/date-select.svelte'
    import Helper from '../_common/helper.svelte'
    import Popover from '../_common/popover.svelte'
    import { ProductMovementsRepository } from '../../modules/posApi/stock/product-movements/product-movements.repository'
    import { ProductMovementsService } from '../../modules/posApi/stock/product-movements/product-movements.service'
    import StockOutForm from './stock-out-form.svelte'
    import type { ProductMovement } from '../../modules/posApi/stock/product-movements/product-movement'
    import { productMovementReasonOptions, ProductMovementReasonEnum } from '../../modules/posApi/stock/product-movements/product-movement-reason.enum'
    import ZentitySelect from '../_common/z/zentity-select.svelte'
    import type { Service } from '../../modules/posApi/services/service'
    import { GridButton, GridClass, GridCol, GridColAlignEnum, GridColTypeEnum, GridLine } from '../_common/z/zgrid'
    import Zgrid from '../_common/z/zgrid.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'

    const rep = new ProductMovementsRepository()
    const service = new ProductMovementsService(rep)

    let personsR = new PersonsRepository()
    let personsS = new PersonsService(personsR)

    let show = false
    let entities: ProductMovement[] = []
    let dates: { from: Date; to: Date } = window.api.utils.dates.today()
    let dateSelect
    let selectedService: Service = null

    onMount(async () => {
        await loadEntities()
    })

    async function loadEntities() {
        try {
            show = true
            entities = []
            const filter = {
                from: dates?.from || undefined,
                to: dates?.to || undefined,
                type: 'out_manual',
                serviceId: selectedService ? selectedService.serviceId : undefined,
            }

            // limita a navegação por mais de 7 dias
            if (!window.store.token.permissions.pos.sales.navigate) {
                const diff = dayjs(new Date()).diff(filter.from, 'days')
                if (diff > 7) {
                    dates.from = dayjs(new Date()).add(-7, 'day').toDate()
                    filter.from = dates.from
                    dateSelect.setDate(dates.from, dates.to)
                }
            }

            entities = await service.getAll(filter)
            // ajuste de balanço não é uma saída de negócio — fica só em "Movimentações" (ver stock-home.svelte)
            entities = entities.filter((el) => el.reason !== ProductMovementReasonEnum.ajuste_inventario)
            let persons: Person[] = []
            let personsId = []
            for (const invoice of entities) {
                personsId.push(invoice.personId)
            }

            let promises = []
            for (let i = 0; i < personsId.length; i += 100) {
                const chunk = personsId.slice(i, i + 100)
                promises.push(personsS.getAll({ persons: chunk.join(',') }))
            }
            const results = await Promise.all(promises)
            for (const result of results) {
                persons = persons.concat(result)
            }
            for (let invoice of entities) {
                invoice.person = persons.find((el) => el.personId === invoice.personId)
            }

            loadGrid()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.showActs = true
        grid.title = 'Saídas realizadas'
        grid.subtitle = 'Apenas as saídas manuais. Para todas as movimentações acesse a aba Estoque e clique em Movimentações.'
        grid.cols.push(new GridCol({ content: 'Data do lançamento', type: GridColTypeEnum.datetime }))
        grid.cols.push(new GridCol({ content: 'Data da saída', type: GridColTypeEnum.datetime }))
        grid.cols.push(new GridCol({ content: 'Pessoa' }))
        grid.cols.push(new GridCol({ content: 'Item' }))
        grid.cols.push(new GridCol({ content: 'Motivo' }))
        grid.cols.push(new GridCol({ content: 'Vlr unit', type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: 'Qtde', type: GridColTypeEnum.int }))
        grid.cols.push(new GridCol({ content: 'Total', type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.createdAt })
            line.slots.push({ content: item.movedAt })
            line.slots.push({ content: item.person?.name })

            line.slots.push({ content: item.service?.name })
            line.slots.push({ content: reasonLabel(item.reason) })
            line.slots.push({ content: item.value })
            line.slots.push({ content: item.quantity })
            line.slots.push({ content: item.value * item.quantity })
            line.slots.push({
                content: [
                    new GridButton({
                        label: 'Editar',
                        icon: faEdit,
                        callback: () => {
                            editEntity(item.productMovementId)
                        },
                    }),
                ],
            })
            grid.lines.push(line)
        }
        grid = grid
    }

    function evaluateError(error) {
        window.error(error)
    }

    function reasonLabel(reason: string) {
        return productMovementReasonOptions.find((el) => el.value === reason)?.label || '-'
    }

    async function dateChanged(event) {
        dates = event.detail
        await loadEntities()
    }

    let showForm = false
    let productMovementId: string = null
    function editEntity(id) {
        productMovementId = id
        showForm = true
    }
    function newEntity() {
        productMovementId = null
        showForm = true
    }
    function formChanged() {
        loadEntities()
        showForm = false
    }
    function formCanceled() {
        showForm = false
    }

    function getSource(item: ProductMovement) {
        if (item.invoiceId) return 'PDV'
        return 'Saídas'
    }

    function selectedServiceChanged() {
        loadEntities()
    }
</script>

{#if showForm}
    <Popover
        title="Saída de Produto/Insumo"
        width="1300px"
        height="400px"
        on:close={() => {
            showForm = false
        }}
    >
        <StockOutForm {productMovementId} on:change={formChanged} on:cancel={formCanceled} />
    </Popover>
{/if}

<Content footer={true}>
    <Zact>
        <div>
            <ZentitySelect
                fonte="servico"
                label="Produto/Insumo"
                params={{ type: 'product,consumable', price: true }}
                bind:value={selectedService}
                on:change={selectedServiceChanged}
            />
        </div>
        <DateSelect bind:this={dateSelect} startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        <div class="spacer"></div>
        {#if window.store.token.permissions.stock.outputs.write}
            <Zbutton color="primary" icon={faPlusCircle} label="Nova saída" mobileHideLabel={true} on:click={newEntity} />
        {/if}
    </Zact>
    <Zgrid class="h-space" {grid}></Zgrid>

    <LoadSpinner {show} />
</Content>

<style>
</style>
